import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { get } from 'lodash-es';

/** Same-origin path: served by rollout assistant nginx (CSP-safe). */
export const UI_CONFIG_JSON_PATH = '/config.json';

export interface UrlsByLanguage {
  de?: string;
  en?: string;
}

export interface UiConfig {
  imprintUrls?: UrlsByLanguage;
}

/** Default external imprint URLs when `imprintUrls` is absent (matches main LMC UI footer imprint links). */
export const DEFAULT_IMPRINT_URLS_BY_LANG: Readonly<Record<'de' | 'en', string>> = {
  de: 'https://rs-nc.rohde-schwarz.com/de/impressum',
  en: 'https://rs-nc.rohde-schwarz.com/en/company-details',
};

@Injectable({
  providedIn: 'root',
})
export class UiConfigService {
  private readonly http = inject(HttpClient);

  private _config: UiConfig = {};

  get config(): UiConfig {
    return this._config;
  }

  init(): Promise<void> {
    return firstValueFrom(
      this.http.get<UiConfig>(UI_CONFIG_JSON_PATH).pipe(
        tap((config) => {
          this._config = config;
        }),
        catchError(() => {
          this._config = {};
          return of(undefined);
        }),
        map(() => undefined),
      ),
    );
  }

  getImprintUrl(lang: string): string {
    const key: 'de' | 'en' = lang === 'de' ? 'de' : 'en';
    return get(this._config.imprintUrls, key) ?? DEFAULT_IMPRINT_URLS_BY_LANG[key];
  }
}
